"use client"

import { JSX, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/hooks/useAuth"
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import LoadingSpinner from "@/components/shared/LoadingSpinner"
import {
  getWeeklyTokenUsage,
  type TokenUsageResponse,
} from "@/services/resourceUsageService"
import TokenUsageCharts from "./TokenUsageCharts"

const PAGE_TITLE = "My Token Usage"

/**
 * TokenUsagePage component.
 *
 * Per-user token usage viewer. Fetches the last seven days of token usage
 * (bucketed by the user's local date) and renders three stacked bar charts —
 * total, input, and output tokens.
 *
 * @returns The rendered Token Usage interface.
 */
export default function TokenUsagePage(): JSX.Element {
  const navigate = useNavigate()
  const { getAccessToken } = useAuth()

  const [usage, setUsage] = useState<TokenUsageResponse | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function fetchUsage(): Promise<void> {
      setIsLoading(true)
      setError(null)
      try {
        const timeZone: string =
          Intl.DateTimeFormat().resolvedOptions().timeZone
        const accessToken = await getAccessToken()
        const data = await getWeeklyTokenUsage(timeZone, accessToken)
        if (!cancelled) {
          setUsage(data)
        }
      } catch (err) {
        if (!cancelled) {
          const message =
            err instanceof Error ? err.message : "Failed to load token usage"
          setError(message)
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchUsage()

    // Guard against setting state after unmount if the request is in flight.
    return () => {
      cancelled = true
    }
  }, [getAccessToken])

  // Whether there is any non-zero usage across the whole window.
  const hasAnyUsage: boolean = useMemo(() => {
    if (!usage) {
      return false
    }
    return usage.usage.some((day) =>
      Object.values(day.agents).some(
        (counts) => counts.totalTokens > 0
      )
    )
  }, [usage])

  return (
    <>
      <title>{PAGE_TITLE} - MAP Apps Platform</title>
      <Card className="flex flex-1 flex-col min-h-0 shadow-none gap-4">
        <CardHeader className="pb-0">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                <h1>{PAGE_TITLE}</h1>
              </CardTitle>
              <CardDescription>
                <p className="text-sm text-muted-foreground mt-1">
                  Your token usage over the last seven days, broken down by agent.
                </p>
              </CardDescription>
            </div>
            {/*
              Admin view — only shown to Resource Usage managers.
            */}
            {usage?.isAdmin && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/computing/resource-usage/token/admin")}
              >
                Admin View
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex flex-1 flex-col min-h-0 gap-4 overflow-auto">
          {isLoading && (
            <div className="flex flex-1 items-center justify-center">
              <LoadingSpinner loadingContent="Loading token usage..." />
            </div>
          )}

          {!isLoading && error && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2">
              <p className="text-lg font-semibold">Couldn't load token usage</p>
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {!isLoading && !error && usage && !hasAnyUsage && (
            <div className="flex flex-1 flex-col items-center justify-center gap-2">
              <p className="text-lg font-semibold">No Usage Yet</p>
              <p className="text-sm text-muted-foreground">
                No token usage recorded in the last seven days.
              </p>
            </div>
          )}

          {!isLoading && !error && usage && hasAnyUsage && (
            <TokenUsageCharts usage={usage} />
          )}
        </CardContent>
      </Card>
    </>
  )
}
